// third-party
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';

// types
import { DefaultRootStateProps, MakeRequired } from 'types';
import { InventorySkuType, ProductMultiCatalog } from 'types/e-commerce';
import { CreateDetailProductType, MerchantProductType, ItemToApprove, ItemToFullyApprove } from 'types/product';
import {
    SaveFacetsProductsProps,
    SearchProductTypeProps,
    getCategoryFacetsProps,
    getProductsProps,
    getProductsThunkHistoryProps,
    getProductsToApproveCountProps,
    getProductsToApproveProps,
    metadata,
    SaveFacetsSkusProps,
    DeleteProductOrSkuFacetsProps
} from 'types/slices/slices';
import { uploadOneImageToSkuProps } from 'types/slices/slices';
import { EditDetailProductType, MerchantType, PricingItemType, skuImageType } from 'types/components/shared';
import { openSnackbar } from 'store/slices/snackbar';
import JSZip from 'jszip';
import { AxiosResponse } from 'axios';
import { AllMerchantsProductDataType } from 'types/views/products/allMerchantsProductData';
import { wrapForNoInteraction } from 'views/Catalog/Products/utils/wrapForNoInteraction';

const initialState: DefaultRootStateProps['product'] = {
    image: '',
    loadingFiles: false,
    files: null,
    error: null,
    productErrors: { product: [], sku: [] },
    products: [],
    product: null,
    relatedProducts: [],
    skus: [],
    loadingSku: true,
    loadingSkus: true,
    selectedSku: null,
    selectedSkus: [],
    categories: [],
    tradePolicies: [],
    approvalStatus: [],
    getRejectedStatus: [],
    approvalHistorial: [],
    reviews: [],
    addresses: [],
    loadingProduct: true,
    merchantProducts: [],
    createProductFacet: [],
    warehouses: [],
    loadingProducts: true,
    loadingMedia: false,
    productsMulticatalog: [],
    productsTotalPages: 0,
    productsFacetsMulti: [],
    uploadedImages: { done: [], pending: [], error: [] },
    totalImagesToLoad: 0,
    creatingProduct: false,
    urlSearch: '',
    productsToApprove: {
        products: [],
        totalCount: 0,
        totalPages: 0,
        loading: false
    },
    bulk: [],
    loadingBulk: false,
    productsToApproveCount: 0,
    loadingErrors: false,
    loadingResetErrors: false,
    productsMulticatalogVendors: { items: [], totalItems: 0 },
    loadingProductsVendors: false,
    loadingMetrics: false,
    metrics: { count: 0, size: 0 },
    collections: [],
    loadingCollections: false,
    additionalEans: [],
    collectionsTotalPages: 0
};

const slice = createSlice({
    name: 'product',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        getProductsPending(state) {
            state.loadingProducts = true;
        },
        // FILTER PRODUCTS
        filterProductsSuccess(state, action) {
            state.products = action.payload;
        },

        // GET PRODUCT
        getProductSuccess(state, action) {
            state.product = action.payload;
        },
        // GET SKUS
        getSkuSuccess(state, action) {
            state.skus = action.payload;
        },
        getCategoriesSuccess(state, action) {
            state.categories = action.payload;
        },
        // GET RELATED PRODUCTS
        getRelatedProductsSuccess(state, action) {
            state.relatedProducts = action.payload;
        },
        // GET PRODUCT REVIEWS
        getProductReviewsSuccess(state, action) {
            state.reviews = action.payload;
        },
        //Get Files
        getFilesSuccess(state, action) {
            state.files = action.payload;
        },
        getImageSuccess(state, action) {
            state.image = action.payload;
        },
        clearUrlSearch(state) {
            state.urlSearch = '';
        }
    },
    extraReducers(builder) {
        builder
            .addCase(saveProduct.pending, (state) => {
                state.creatingProduct = true;
            })
            .addCase(saveProduct.fulfilled, (state) => {
                state.creatingProduct = false;
            });

        builder
            .addCase(approvalStatus.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(approvalStatus.rejected, (state) => {
                state.loadingProducts = false;
                state.approvalStatus = [];
            })
            .addCase(approvalStatus.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.approvalStatus = action.payload.response.skuApprovalStatus;
            });

        builder
            .addCase(getRejectedStatus.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getRejectedStatus.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.getRejectedStatus = action.payload.response;
            });
        builder
            .addCase(getApprovalHistorial.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getApprovalHistorial.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.approvalHistorial = action.payload.response;
            });
        builder
            .addCase(getSku.pending, (state) => {
                state.loadingSku = true;
            })
            .addCase(getSku.fulfilled, (state, action) => {
                state.loadingSku = false;
                state.selectedSku = action.payload.response;
            });
        builder
            .addCase(getSkusMulticatalogService.pending, (state) => {
                state.loadingSkus = true;
            })
            .addCase(getSkusMulticatalogService.fulfilled, (state, action) => {
                state.loadingSkus = false;
                state.selectedSkus = action.payload.response;
            });
        /* ------------------------------------------- */
        /* --------- MULTI MERCHANT SERVICES --------- */
        /* ------------------------------------------- */
        builder
            // PRODUCT
            .addCase(getProductDetails.pending, (state) => {
                state.loadingProduct = true;
            })
            .addCase(getProductDetails.fulfilled, (state, action) => {
                state.loadingProduct = false;
                state.merchantProducts = action.payload;
            });
        // SKUs
        builder
            .addCase(getProductSkuList.pending, (state) => {
                state.loadingProduct = true;
            })
            .addCase(getProductSkuList.fulfilled, (state) => {
                state.loadingProduct = false;
            });
        builder
            .addCase(getWarehouses.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getWarehouses.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.warehouses = action.payload.response;
            });

        builder
            .addCase(uploadOneImageToSku.pending, (state, action) => {
                state.uploadedImages.pending.push(action.meta.arg.file.name);
            })
            .addCase(uploadOneImageToSku.fulfilled, (state, action) => {
                state.uploadedImages.done.push({ ...action.payload.data.response, name: action.meta.arg.file.name });
            })
            .addCase(uploadOneImageToSku.rejected, (state, action) => {
                state.uploadedImages.error.push({
                    skuId: action.meta.arg.skuId,
                    name: action.meta.arg.file.name,
                    error: JSON.stringify(action?.error ?? { error: 'Unknown error' })
                });
            });

        builder
            .addCase(saveFacetsProducts.pending, (state) => {
                state.loadingMedia = true;
            })
            .addCase(saveFacetsProducts.fulfilled, (state) => {
                state.loadingMedia = false;
            });
        builder.addCase(getTradePolicies.fulfilled, (state, action) => {
            state.tradePolicies = action.payload;
        });
        builder
            .addCase(getProductsMulticatalog.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getProductsMulticatalog.fulfilled, (state, action) => {
                state.productsMulticatalog = action.payload.response.map((item: ProductMultiCatalog) => ({
                    ...item,
                    title: item.productName
                }));
                state.loadingProducts = false;
                state.productsTotalPages = action.payload.totalPages;
            })
            .addCase(getProductsMulticatalog.rejected, (state, action) => {
                state.loadingProducts = false;
            });
        builder
            .addCase(getProductsToApprove.pending, (state) => {
                state.productsToApprove.loading = true;
            })
            .addCase(getProductsToApprove.fulfilled, (state, action) => {
                state.productsToApprove.products = action.payload.data;
                state.productsToApprove.totalCount = state.productsToApprove.products.length;
                state.productsToApprove.totalPages = action.payload.headers['x-total-pages'];
                state.productsToApprove.loading = false;
            })
            .addCase(getProductsToApproveCount.fulfilled, (state, action) => {
                state.productsToApproveCount = action.payload;
            });
        builder
            .addCase(getCollections.pending, (state) => {
                state.loadingCollections = true;
            })
            .addCase(getCollections.fulfilled, (state, action) => {
                state.loadingCollections = false;
                const collections = Array.isArray(action.payload?.response) ? action.payload.response : [];
                state.collections = collections.map((item: any) => ({
                    ...item,
                    collectionVtexId: item.collectionVtexId ?? item.CollectionVtexId,
                    skuIds: Array.isArray(item?.skuIds)
                        ? item.skuIds
                        : typeof item?.skuIds === 'string'
                        ? item.skuIds
                              .split(',')
                              .map((value: string) => Number(value.trim()))
                              .filter((value: number) => !Number.isNaN(value))
                        : [],
                    productIds: Array.isArray(item?.productIds)
                        ? item.productIds
                        : typeof item?.productIds === 'string'
                        ? item.productIds
                              .split(',')
                              .map((value: string) => Number(value.trim()))
                              .filter((value: number) => !Number.isNaN(value))
                        : [],
                    totalSku: Number.isFinite(Number(item?.totalSku))
                        ? Number(item.totalSku)
                        : Array.isArray(item?.skuIds)
                        ? item.skuIds.length
                        : 0,
                    totalProduct: Number.isFinite(Number(item?.totalProduct))
                        ? Number(item.totalProduct)
                        : Array.isArray(item?.productIds)
                        ? item.productIds.length
                        : 0
                }));
                state.collectionsTotalPages = Number.isFinite(Number(action.payload?.totalPages)) ? Number(action.payload.totalPages) : 0;
            })
            .addCase(saveFacetsSkus.fulfilled, (state) => {
                state.loadingMedia = false;
            });
        builder
            .addCase(getFiles.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(getFiles.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(getFiles.rejected, (state) => {
                state.loadingFiles = false;
            })
            .addCase(getAssets.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(getAssets.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(getAssets.rejected, (state) => {
                state.loadingFiles = false;
            });
        builder
            .addCase(getProductErrors.pending, (state, action) => {
                state.loadingErrors = true;
            })
            .addCase(getProductErrors.fulfilled, (state, action) => {
                state.productErrors = action.payload.data;
                state.loadingErrors = false;
            });
        builder
            .addCase(resetProductErrors.pending, (state, action) => {
                state.loadingResetErrors = true;
            })
            .addCase(resetProductErrors.fulfilled, (state, action) => {
                state.productErrors = { product: [], sku: [] };
                state.loadingResetErrors = false;
            });
        builder
            .addCase(createFile.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(createFile.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(createFile.rejected, (state) => {
                state.loadingFiles = false;
            })
            .addCase(deleteAssetsSlice.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(deleteAssetsSlice.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(deleteAssetsSlice.rejected, (state) => {
                state.loadingFiles = false;
            })
            .addCase(deleteFilesSlice.pending, (state) => {
                state.loadingFiles = true;
            })
            .addCase(deleteFilesSlice.fulfilled, (state) => {
                state.loadingFiles = false;
            })
            .addCase(deleteFilesSlice.rejected, (state) => {
                state.loadingFiles = false;
            });
        builder
            .addCase(getProductsThunkHistory.pending, (state) => {
                state.loadingBulk = true;
            })
            .addCase(getProductsThunkHistory.fulfilled, (state, action) => {
                state.bulk = action.payload;
                state.loadingBulk = false;
            });
        builder
            .addCase(getAdditionalEans.pending, (state) => {
                state.loadingProducts = true;
            })
            .addCase(getAdditionalEans.fulfilled, (state, action) => {
                state.additionalEans = action.payload;
                state.loadingProducts = false;
            });
        builder
            .addCase(getBucketMetrics.pending, (state) => {
                state.loadingMetrics = true;
            })
            .addCase(getBucketMetrics.fulfilled, (state, action) => {
                state.metrics = action.payload;
                state.loadingMetrics = false;
            })
            .addCase(getBucketMetrics.rejected, (state, action) => {
                state.loadingMetrics = false;
            })
            .addCase(getBucketAssetsMetrics.pending, (state) => {
                state.loadingMetrics = true;
            })
            .addCase(getBucketAssetsMetrics.fulfilled, (state, action) => {
                state.metrics = action.payload;
                state.loadingMetrics = false;
            })
            .addCase(getBucketAssetsMetrics.rejected, (state, action) => {
                state.loadingMetrics = false;
            });
    }
});

export default slice.reducer;
export const { hasError } = slice.actions;

// ----------------------------------------------------------------------
export const getProductsToApprove = createAsyncThunk(
    `${slice.name}/getProductsToApprove`,
    async ({ idMerchant, statusId, email, categoryId, approvalStatusId, brandId, vendorId, userId }: getProductsToApproveProps) => {
        const response = await axios.get(`/loolu/api/approval/`, {
            params: {
                merchantId: idMerchant,
                statusId,
                email,
                categoryId,
                brandId,
                approvalStatusId,
                vendorId,
                userId
            }
        });
        const { data, headers } = response;
        return { data, headers };
    }
);

export const getCollections = createAsyncThunk(
    `${slice.name}/getCollections`,
    async ({
        merchantId,
        collectionId,
        name,
        page,
        limit
    }: {
        merchantId: number;
        collectionId?: number;
        name?: string;
        page?: number;
        limit?: number;
    }) => {
        const response = await axios.get('/loolu/api/collection/search', {
            params: {
                merchantId,
                collectionId,
                name,
                page,
                limit
            }
        });
        return response.data;
    }
);

export const saveCollection = async (collection: {
    name: string;
    description: string;
    searchable: boolean;
    highlight: boolean;
    dateFrom: string; // '2025-06-20';
    dateTo: string; //'2025-07-20';
    merchantId: number;
}) => {
    const response = await axios.post('/loolu/api/collection/save', collection);
    return response.data;
};

export const updateCollection = async (collection: {
    collectionId: number;
    merchantId: number;
    name: string;
    description: string;
    searchable: boolean;
    highlight: boolean;
    dateFrom: string;
    dateTo: string;
}) => {
    const response = await axios.put('/loolu/api/collection/save', collection);
    return response.data;
};

export const associateSkusToCollection = async (payload: {
    collectionId: number;
    merchantId: number;
    skuIds?: Array<number | string>;
    productIds?: Array<number | string>;
}) => {
    const response = await axios.post('/loolu/api/collection/save/associate-sku', payload);
    return response.data;
};

export const deleteCollectionSkuAssociation = async (payload: {
    collectionId: number;
    merchantId: number;
    skuIds?: Array<number | string>;
    productIds?: Array<number | string>;
}) => {
    const response = await axios.post('/loolu/api/collection/delete/associate-sku', payload);
    return response.data;
};

export const deleteCollection = async ({ collectionId, merchantId }: { collectionId: number; merchantId: number }) => {
    const response = await axios.delete('/loolu/api/collection/delete', {
        params: {
            collectionId,
            merchantId
        }
    });
    return response.data;
};

export const getProductsToApproveCount = createAsyncThunk(
    `${slice.name}/getProductsToApproveCount`,
    async ({ statusId, email, providerId }: getProductsToApproveCountProps) => {
        const response = await axios.get(`/loolu/api/approval/total`, {
            params: {
                statusId,
                email,
                providerId
            }
        });

        return response.data.total;
    }
);

export const getProductsThunk = createAsyncThunk(
    `${slice.name}/getProducts`,
    async ({ idMerchant, page, productName, idSKU, idBrand, idCategory, eans }: getProductsProps) => {
        const response = await axios.get(`/loolu/api/massive/search/`, {
            params: {
                idMerchant: idMerchant || 1,
                page,
                productName: productName || null,
                idSKU,
                idBrand,
                idCategory,
                eans
            }
        });
        return response.data;
    }
);

export const getProductsThunkHistory = createAsyncThunk(
    `${slice.name}/getProductsThunkHistory`,
    async ({ idMerchant, startDate, endDate }: getProductsThunkHistoryProps) => {
        const response = await axios.get(`/loolu/api/bulk`, {
            params: {
                merchantId: idMerchant || 1,
                startDate,
                endDate
            }
        });
        return response.data;
    }
);

export const getProductsMulticatalogVendors = async ({ merchantId, providerId, page }: SearchProductTypeProps) => {
    const response = await axios.get('/loolu/api/multicatalog/product/searchBy', {
        params: {
            merchantId,
            providerId,
            page,
            limit: 999
        }
    });
    return response.data;
};
export const getProductsFilterResults = async ({
    skuRefIds,
    textLink,
    merchantId,
    providerId,
    productName,
    eans,
    productRefIds,
    skuIds,
    productIds,
    categoryId,
    brandId,
    page,
    limit
}: SearchProductTypeProps) => {
    const response = await axios.get('/loolu/api/multicatalog/product/search', {
        params: {
            skuRefIds,
            textLink,
            merchantId,
            providerId,
            productName,
            eans,
            productRefIds,
            skuIds,
            productIds,
            categoryId,
            brandId,
            page,
            limit: limit || 12
        }
    });
    return response.data;
};

export const getProductMultiCatalogURL = '/loolu/api/multicatalog/product/searchBy';

export const getProductsMulticatalog = createAsyncThunk(
    `${slice.name}/getProductsMulticatalog`,
    async (
        {
            skuRefIds,
            textLink,
            providerId,
            productName,
            eans,
            productRefIds,
            skuIds,
            productIds,
            categoryId,
            brandId,
            page,
            limit,
            isActive
        }: SearchProductTypeProps,
        { rejectWithValue }
    ) => {
        try {
            const response = await axios.get(getProductMultiCatalogURL, {
                params: {
                    skuRefIds,
                    textLink,
                    providerId,
                    productName,
                    eans,
                    productRefIds,
                    skuIds,
                    productIds,
                    categoryId,
                    brandId,
                    page,
                    limit: limit || '',
                    isActive
                }
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.message);
        }
    }
);

export const getCategoryFacets = createAsyncThunk(
    `${slice.name}/getCategoryFacets`,
    async ({ merchantId, categoryId }: getCategoryFacetsProps) => {
        const response = await axios.get(`/loolu/api/catalog/facets/fv/merchant/${merchantId}/category/${categoryId}`);
        return response.data;
    }
);

export const saveFacetsProducts = createAsyncThunk(`${slice.name}/saveFacetsProducts`, async (data: SaveFacetsProductsProps) => {
    const { merchantId, productId, dataFacets } = data;

    const response = await axios.post(`/loolu/api/linkvariant/product`, dataFacets, {
        params: {
            idMerchant: merchantId,
            productId
        }
    });
    return response.data;
});

export const deleteProductOrSkuFacets = createAsyncThunk(
    `${slice.name}/deleteProductOrSkuFacets`,
    async (data: DeleteProductOrSkuFacetsProps) => {
        const { merchantId, productId, dataFacets, isSku, skuId } = data;

        const body = dataFacets?.map((facet) => ({
            ...facet,
            ...(isSku ? { skuId } : { productId })
        }));

        const response = await axios.delete(`/loolu/api/linkvariant/remove-variants/${isSku ? 'sku' : 'product'}`, {
            data: body,
            params: {
                idMerchant: merchantId,
                ...(isSku ? { skuId } : { productId })
            }
        });
        return response.data;
    }
);

export const saveFacetsSkus = createAsyncThunk(`${slice.name}/saveFacetsSkus`, async (data: SaveFacetsSkusProps) => {
    const { merchantId, skuId, dataFacets } = data;

    const response = await axios.post(`/loolu/api/linkvariant/sku`, dataFacets, {
        params: {
            idMerchant: merchantId,
            skuId
        }
    });
    return response.data;
});

const retryService = async (idSKU: number, idMerchant: number, reintentos: number): Promise<any> => {
    const maxRetries = 5;
    if (reintentos >= maxRetries) {
        throw new Error('Se alcanzó el límite de reintentos');
    }

    const response = await axios.get('/loolu/api/product/detail/sku', {
        params: {
            idMerchant,
            idSKU
        }
    });
    if (response.data.code === 200) {
        reintentos = 0;
        return response;
    }
    reintentos += 1;
    return retryService(idSKU, idMerchant, reintentos);
};

export const getSku = createAsyncThunk(`${slice.name}/getSku`, async ({ idSKU, idMerchant }: any): Promise<any> => {
    const reintentos = 0;
    const response = await retryService(idSKU, idMerchant, reintentos);
    return response?.data;
});

export const saveProduct = createAsyncThunk(
    `${slice.name}/saveProduct`,
    async (productBody: AllMerchantsProductDataType<CreateDetailProductType>) => {
        try {
            const response = await axios.post('/loolu/api/product/save/product', productBody.detailProduct, {});
            setTimeout(() => {}, 2000);
            return response.data;
        } catch (e: any) {
            return e;
        }
    }
);

export const getTradePolicies = createAsyncThunk(`${slice.name}/getTradePolicies`, async (idMerchant: number) => {
    try {
        const response = await axios.get('/loolu/api/multicatalog/tradepolicies', {
            params: {
                idMerchant
            }
        });
        return response.data.response;
    } catch (error: any) {
        return error.response.data;
    }
});

export const approvalStatus = createAsyncThunk(`${slice.name}/lifecycleapproval`, async (idMerchant: number | null) => {
    const response = await axios.get('/loolu/api/product/lifecycleapproval', {
        params: {
            idMerchant: idMerchant
        }
    });
    return response.data;
});

export const getApprovalStepsByMerchant = async (idMerchant: number) => {
    const response = await axios.get('/loolu/api/product/lifecycleapproval', {
        params: {
            idMerchant: idMerchant
        }
    });
    return response.data.response.skuApprovalStatus;
};

export const getRejectedStatus = createAsyncThunk(`${slice.name}/rejectedstatus`, async ({ idMerchant }: { idMerchant: number }) => {
    const response = await axios.get('/loolu/api/product/rejectcatalog', {
        params: {
            idMerchant: idMerchant || 1
        }
    });
    return response.data;
});

export const fullyApproveProduct = async ({ fullyApproveProductPayload }: { fullyApproveProductPayload: ItemToFullyApprove }) => {
    const response = await axios.post('/loolu/api/product/save/approve-all', fullyApproveProductPayload);

    return response.data;
};

export const saveApprovalStatus = async ({
    idMerchant,
    saveApprovalPayload
}: {
    idMerchant: number;
    saveApprovalPayload: ItemToApprove;
}) => {
    const response = await axios.post('/loolu/api/product/save/approval', saveApprovalPayload, {
        params: {
            idMerchant
        }
    });

    return response.data;
};

export const saveApproveAll = createAsyncThunk(`${slice.name}/saveApproveAll`, async (payload: any) => {
    const response = await axios.post('/loolu/api/multicatalog/product/save/approve', [payload], {});
    return response.data;
});

export const getApprovalHistorial = createAsyncThunk(
    `${slice.name}/approvalhistorial`,
    async ({ skuId, prodId, idMerchant }: { skuId: string | null; prodId: string | number | undefined; idMerchant: any }) => {
        const response = await axios.get('/loolu/api/product/approval', {
            params: {
                skuId,
                prodId,
                idMerchant
            }
        });
        return response.data;
    }
);

/* ------------------------------------------- */
/* --------- MULTI MERCHANT SERVICES --------- */
/* ------------------------------------------- */

export const getProductDetails = createAsyncThunk(
    `${slice.name}/getMultiMerchantProduct`,
    async ({
        idProd,
        merchants,
        availableMerchantsForProduct
    }: {
        idProd: number | string;
        merchants: MerchantType[];
        availableMerchantsForProduct: Partial<MerchantType>[];
    }) => {
        const resolvedPromises: any = [];
        try {
            for (const { merchantId } of merchants) {
                if (!availableMerchantsForProduct.find((availableMerchant) => availableMerchant.merchantId === merchantId)) continue;

                // eslint-disable-next-line no-await-in-loop
                const skuListResult = await axios.get(`/loolu/api/product/detail/productSkus`, {
                    params: {
                        idProd,
                        merchantId
                    }
                });

                // Si la respuesta tiene código 501, saltamos este merchant
                if (skuListResult.data?.code === 501) {
                    console.warn(`Merchant ${merchantId} returned code 501, skipping.`);
                    continue;
                }

                const additionalCategories = await axios.get('/loolu/api/similarcategory/search', {
                    params: {
                        productId: idProd,
                        merchantId
                    }
                });

                const originalSkus = skuListResult.data?.response?.skus;

                if (originalSkus?.length) {
                    const filteredSkus = originalSkus.filter((sku: any) => sku.skuID !== '0');
                    try {
                        const additionalEans = await axios.get(`/loolu/api/additionalSkuEans/search`, {
                            params: {
                                skuId: filteredSkus[0].skuID,
                                merchantId
                            }
                        });
                        filteredSkus[0].additionalEans = additionalEans.data.response.eans;
                    } catch (error) {
                        console.error(error);
                    }
                    skuListResult.data.response.skus = filteredSkus;

                    skuListResult.data.response.additionalCategories = additionalCategories.data?.response.map(
                        ({ categoryId, categoryName }: { categoryId: number; categoryName: string }) => ({
                            categoryId,
                            categoryName
                        })
                    );
                }

                resolvedPromises.push(skuListResult);
            }

            const result: MerchantProductType[] = resolvedPromises.map(({ data: { response } }: any) => {
                const foundMerchant = merchants.find((m) => m.merchantId === response.merchantId);
                if (!foundMerchant) return [];
                return {
                    merchantId: foundMerchant.merchantId,
                    merchantName: foundMerchant.name,
                    isFather: foundMerchant.isFather,
                    detailProduct: { ...response, isfatherMerchantId: foundMerchant.isFather }
                };
            });

            const cleanedResults = result.filter((item) => {
                if (!item.merchantId) return false;
                return true;
            });

            const filteredResults: MerchantProductType[] = [];

            cleanedResults.forEach((item) => {
                if (!filteredResults.find((itemA) => itemA.merchantId === item.merchantId)) {
                    filteredResults.push(item);
                }
            });

            return filteredResults;
        } catch (error) {
            return [];
        }
    }
);

export const getSkusMulticatalogService = createAsyncThunk(
    `${slice.name}/getSkusMulticatalogService`,
    async ({ idSku, idMerchant }: { idSku: number | string; idMerchant: number | string }) => {
        const response = await axios.get('/loolu/api/product/detail/sku-multicatalog', {
            params: {
                idSKU: idSku,
                idMerchant
            }
        });

        return response.data;
    }
);

export const getProductSkuList = createAsyncThunk(
    `${slice.name}/getMultiMerchantProductSKUs`,
    async ({ idProd, merchantId }: { idProd: number | string; merchantId: number | string }) => {
        const response = await axios.get('/loolu/api/product/detail/productSkus', {
            params: {
                idProd,
                merchantId
            }
        });
        return response.data.response;
    }
);

export const getWarehouses = createAsyncThunk(
    `${slice.name}/getMultiMerchantWarehouses`,
    async ({ warehouses, idMerchant }: { warehouses?: number | string; idMerchant: number | string }) => {
        const response = await axios.get('/loolu/api/multicatalog/warehouses', {
            params: {
                idMerchant,
                warehouses
            }
        });

        return response.data;
    }
);

export const updateInventoriesService = createAsyncThunk(
    `${slice.name}/updateInventories`,
    async ({ inventories }: { inventories: MakeRequired<InventorySkuType, 'stockInVTEX' | 'refID'>[] }) => {
        const response = await axios.post('/loolu/api/inventory', inventories, {});

        return response.data;
    }
);

export const getFiles = createAsyncThunk(
    `${slice.name}/getFiles`,
    async (
        {
            productId,
            firstLevelRoute,
            merchantId,
            secondLevelRoute
        }: {
            productId: string | number;
            firstLevelRoute?: string;
            merchantId: number;
            secondLevelRoute?: string;
        },
        { dispatch }
    ) => {
        const response = await axios.get(
            `/loolu/api/files/${productId}${firstLevelRoute ? `/${firstLevelRoute}` : ''}${
                secondLevelRoute ? `/${secondLevelRoute}` : ''
            }?merchantId=${merchantId}`
        );

        dispatch(slice.actions.getFilesSuccess(response.data));

        return response.data;
    }
);

export const getAssets = createAsyncThunk(
    `${slice.name}/getAssets`,
    async ({ firstLevelRoute, secondLevelRoute }: { firstLevelRoute?: string; secondLevelRoute?: string }, { dispatch }) => {
        const response = await axios.get(
            `/loolu/api/assets/${firstLevelRoute ? `/${firstLevelRoute}` : ''}${secondLevelRoute ? `/${secondLevelRoute}` : ''}`
        );

        dispatch(slice.actions.getFilesSuccess(response.data));

        return response.data;
    }
);

export const getBucketMetrics = createAsyncThunk(`${slice.name}/getBucketMetrics`, async () => {
    const response = await axios.get(`/loolu/api/files/bucketMetrics`);
    return response.data;
});

export const getBucketAssetsMetrics = createAsyncThunk(`${slice.name}/getBucketAssetsMetrics`, async () => {
    const response = await axios.get(`/loolu/api/assets/bucketMetrics`);
    return response.data;
});

export const getProductErrors = createAsyncThunk(
    `${slice.name}/getProductErrors`,
    async ({ merchantId, productId }: { merchantId: string | number; productId?: string }) => {
        const response = await axios.get(
            productId
                ? `/loolu/api/catalog/products/${productId}/errors?merchantId=${merchantId}`
                : `/loolu/api/catalog/products/errors?merchantId=${merchantId}`
        );

        return response;
    }
);

export const resetProductErrors = createAsyncThunk(
    `${slice.name}/resetProductErrors`,
    async ({ merchantId, productId }: { merchantId: string | number | null; productId?: string }) => {
        const response = await axios.delete(`/loolu/api/catalog/products/${productId}/errors?merchantId=${merchantId}`);

        return response;
    }
);

export const getImage = createAsyncThunk(
    `${slice.name}/downloadFiles`,
    async ({ path, merchantId }: { path: string; merchantId: string | number }, { dispatch }) => {
        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`, {
            responseType: 'arraybuffer'
        });

        const base64 = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));

        dispatch(slice.actions.getImageSuccess(base64));
    }
);

export const getImageAssets = createAsyncThunk(`${slice.name}/getImageAssets`, async ({ path }: { path: string }, { dispatch }) => {
    const response = await axios.get(`/loolu/api/assets/${path}`, {
        responseType: 'arraybuffer'
    });

    const base64 = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));

    dispatch(slice.actions.getImageSuccess(base64));
});

export const editMetadata = createAsyncThunk(
    `${slice.name}/editMetadata`,
    async ({ path, merchantId, metadata }: { path: string; merchantId: string | number | null; metadata: metadata }) => {
        await axios.patch(`/loolu/api/files/${path}?merchantId=${merchantId}`, metadata);
    }
);

export const editMetadataAssets = createAsyncThunk(
    `${slice.name}/editMetadataAssets`,
    async ({ path, metadata }: { path: string; metadata: metadata }) => {
        await axios.patch(`/loolu/api/assets/${path}`, metadata);
    }
);

export const setPublicImageFiles = createAsyncThunk(
    `${slice.name}/setPublicImageFiles`,
    async ({
        path,
        merchantId,
        metadata,
        isPublic
    }: {
        path: string;
        merchantId: string | number | null;
        metadata: metadata;
        isPublic: boolean;
    }) => {
        await axios.patch(`/loolu/api/files/${path}?merchantId=${merchantId}&${isPublic ? 'publish' : 'unPublish'}=true`, metadata);
    }
);

export const setPublicImageAssets = createAsyncThunk(
    `${slice.name}/setPublicImageAssets`,
    async ({ path, metadata, isPublic }: { path: string; metadata: metadata; isPublic: boolean }) => {
        await axios.patch(`/loolu/api/assets/${path}?${isPublic ? 'publish' : 'unPublish'}=true`, metadata);
    }
);

export const downloadFilesAssetsSlice = createAsyncThunk(`${slice.name}/downloadFilesAssetsSlice`, async ({ path }: { path: string }) => {
    const response = await axios.get(`/loolu/api/assets/${path}`, {
        responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${path.split('/').pop()}`);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
});

export const downloadFilesSlice = createAsyncThunk(
    `${slice.name}/downloadFilesSlice`,
    async ({ path, merchantId }: { path: string; merchantId: string | number }) => {
        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`, {
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${path.split('/').pop()}`);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
);

export const downloadFilesZip = createAsyncThunk(
    `${slice.name}/downloadFiles`,
    async ({ paths, merchantId }: { paths: any[]; merchantId: string | number }) => {
        const zip = new JSZip();

        await Promise.all(
            paths.map(async (path) => {
                const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`, {
                    responseType: 'blob'
                });

                const blob = await response.data;
                zip.file(`${path.split('/').pop()}`, blob);
            })
        );

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(zipBlob);
        a.download = 'images.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
);

export const downloadFilesAssetsZip = createAsyncThunk(`${slice.name}/downloadFilesAssetsZip`, async ({ paths }: { paths: any[] }) => {
    const zip = new JSZip();

    await Promise.all(
        paths.map(async (path) => {
            const response = await axios.get(`/loolu/api/assets/${path}`, {
                responseType: 'blob'
            });

            const blob = await response.data;
            zip.file(`${path.split('/').pop()}`, blob);
        })
    );

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(zipBlob);
    a.download = 'images.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
});

export const deleteFilesSlice = createAsyncThunk(
    `${slice.name}/deleteFiles`,
    async ({ path, merchantId }: { path: string; merchantId: string | number }, { dispatch }) => {
        const response = await axios.delete(`/loolu/api/files/${path}?merchantId=${merchantId}`);
        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const deleteAssetsSlice = createAsyncThunk(`${slice.name}/deleteAssetsSlice`, async ({ path }: { path: string }, { dispatch }) => {
    const response = await axios.delete(`/loolu/api/assets/${path}`);

    dispatch(slice.actions.getFilesSuccess(response.data));
});

export const createFolderSlice = createAsyncThunk(
    `${slice.name}/createFolder`,
    async ({ path, merchantId, directoryName }: { path: string; merchantId: string | number; directoryName: string }, { dispatch }) => {
        await axios.post(`/loolu/api/files/${path}?merchantId=${merchantId}`, { directoryName });

        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`);

        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const createFolderAssetsSlice = createAsyncThunk(
    `${slice.name}/createFolderAssetsSlice`,
    async ({ path, directoryName }: { path: string; directoryName: string }, { dispatch }) => {
        await axios.post(`/loolu/api/assets/${path}`, { directoryName });

        const response = await axios.get(`/loolu/api/assets/${path}`);

        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const createFile = createAsyncThunk(
    `${slice.name}/createFile`,
    async ({ path, merchantId, fileList }: { path: string; merchantId: string | number; fileList: FileList }, { dispatch }) => {
        const files = Array.from(fileList);
        const formData = new FormData();

        files.forEach((file) => {
            formData.append('files', file);
        });

        const metadata = files.map((file) => ({ id: file.name }));
        formData.append('metadata', JSON.stringify(metadata));

        await axios.post(`/loolu/api/files/${path}?merchantId=${merchantId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        const response = await axios.get(`/loolu/api/files/${path}?merchantId=${merchantId}`);
        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const createAssetFile = createAsyncThunk(
    `${slice.name}/createAssetFile`,
    async ({ path, fileList }: { path: string; fileList: FileList }, { dispatch }) => {
        const files = Array.from(fileList);
        const formData = new FormData();

        files.forEach((file) => {
            formData.append('files', file);
        });

        const metadata = files.map((file) => ({ id: file.name }));
        formData.append('metadata', JSON.stringify(metadata));

        await axios.post(`/loolu/api/assets/${path}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        const response = await axios.get(`/loolu/api/assets/${path}`);
        dispatch(slice.actions.getFilesSuccess(response.data));
    }
);

export const updateImagesToSku = createAsyncThunk(
    `${slice.name}/updateImagesToSku`,
    async ({
        images,
        skuId,
        merchantId
    }: {
        images: { IdImage: number; ImageURL?: string; Label?: string; Text?: string }[];
        skuId: number | string;
        merchantId: number;
    }) => {
        const imagesList = images.map((item, index) => ({
            IdImage: item.IdImage,
            Label: item.Label ?? '',
            Text: item.Text ?? ''
        }));

        const uploadResponse = await axios.post(`/loolu/api/files/img/${skuId}`, imagesList, {
            params: {
                merchantId
            }
        });

        return uploadResponse;
    }
);

export const uploadOneImageToSku = createAsyncThunk(
    `${slice.name}/uploadOneImageToSku`,
    async ({ skuId, idMerchant, file, isMain = false }: uploadOneImageToSkuProps) => {
        const fileFormData = new FormData();

        // Test errors
        // eslint-disable-next-line no-nested-ternary
        const fileToUpload = file;

        fileFormData.append('isMain', `${isMain}`);
        fileFormData.append('skuId', `${skuId}`);
        fileFormData.append('file', fileToUpload);
        fileFormData.append('idMerchant', `${idMerchant}`);
        fileFormData.append('imageName', file.name);
        fileFormData.append('label', new Date().toISOString());

        const uploadResponse = await axios.post('/loolu/api/images/save/file', fileFormData, {});

        return uploadResponse;
    }
);

export const deleteOneImageToSku = createAsyncThunk(`${slice.name}/deleteImage`, async (image: skuImageType) => {
    const deleteResponse = await axios.delete('/loolu/api/images/delete/file', {
        params: {
            idImage: image.IdImage,
            idMerchant: image.IdMerchant,
            idSKU: image.SkuID
        }
    });

    return deleteResponse;
});

export const updatePricingService = createAsyncThunk(`${slice.name}/updatePrice`, async ({ prices }: { prices: PricingItemType[] }) => {
    const pricesData = prices.map((item) => ({
        idMerchant: Number(item.idMerchant),
        price: Number(item.price) ?? 1,
        priceDiscount: Number(item.priceDiscount) ?? 1,
        refID: Number(item.skuId),
        tradePolicy: item.tradePolicy
    }));

    const response = await axios.post('/loolu/api/price', pricesData, {});

    return response.data;
});

export const saveMultipleSKU = createAsyncThunk(
    `${slice.name}/saveMultipleSKU`,
    async ({ products }: { products: { [key: string]: any }[] }) => {
        // MODIFY TO SAVE SKU
        const requests = products.map((detailProduct: any) => {
            const product = {
                ...detailProduct,
                sku: {
                    ...detailProduct.sku
                }
            };

            return axios.post('/loolu/api/product/save/product', product, {});
        });

        const responses = await Promise.all(requests);
        return responses.map((response) => response.data);
    }
);

type SaveProductResponse = {
    code: number;
    status: string | null;
    message: string;
    response: {
        skuID: string;
        prodID: string;
    };
};

export const saveMultipleProducts = createAsyncThunk(
    `${slice.name}/saveMultipleProducts`,

    async (products: AllMerchantsProductDataType<EditDetailProductType | CreateDetailProductType>[], { dispatch }) => {
        const requests = products.map(({ detailProduct }) => {
            const productData = {
                ...detailProduct,
                departmentId: detailProduct?.categoryId,
                description: wrapForNoInteraction(detailProduct?.description)
            };

            return axios.post<SaveProductResponse>('/loolu/api/product/save/product', productData, {});
        });
        const responses = await Promise.allSettled(requests);

        const successfulResponses = responses
            .filter((response) => response.status === 'fulfilled')
            .map((response) => (response as PromiseFulfilledResult<AxiosResponse<SaveProductResponse, any>>).value.data);

        const failedProducts = products.filter((_, index) => responses[index].status === 'rejected');

        if (failedProducts.length) {
            const errorMessage = failedProducts.reduce((acc, el) => {
                if (acc !== 'Product update failed at merchant ') {
                    acc += ', ';
                }

                acc += el.merchantName;
                return acc;
            }, 'Product update at merchant ');

            dispatch(
                openSnackbar({
                    message: errorMessage,
                    variant: 'error'
                })
            );
        }

        return successfulResponses;
    }
);

export const saveAdditionalCategories = createAsyncThunk(
    `${slice.name}similarcategory/save`,
    async ({ productId, merchantId, categoryIds }: { productId: number; merchantId: number; categoryIds: number[] }) => {
        const response = await axios.post(`/loolu/api/similarcategory/save`, {
            productId,
            merchantId,
            categoryIds
        });
        return response.data;
    }
);

export const deleteAdditionalCategories = createAsyncThunk(
    `${slice.name}similarcategory/delete`,
    async ({ productId, merchantId, categoryIds }: { productId: number; merchantId: number; categoryIds: number[] }) => {
        const response = await axios.post(`/loolu/api/similarcategory/delete`, {
            productId,
            merchantId,
            categoryIds
        });
        return response.data;
    }
);

export const getAdditionalEans = createAsyncThunk(
    `${slice.name}additionalEans`,
    async ({ skuId, merchantId }: { skuId: number; merchantId: number }) => {
        const response = await axios.get(`/loolu/api/additionalSkuEans/search`, {
            params: {
                skuId,
                merchantId
            }
        });
        return response.data.response.eans;
    }
);

export const saveAdditionalEans = createAsyncThunk(
    `${slice.name}additionalEans/save`,
    async ({ skuId, merchantId, eans }: { skuId: number; merchantId: number; eans: string[] }) => {
        const response = await axios.post(`/loolu/api/additionalSkuEans/save`, {
            skuId,
            merchantId,
            eans
        });
        return response.data;
    }
);

export const deleteAdditionalEans = createAsyncThunk(
    `${slice.name}additionalEans/delete`,
    async ({ skuId, merchantId, eans }: { skuId: number; merchantId: number; eans: string[] }) => {
        const response = await axios.post(`/loolu/api/additionalSkuEans/delete`, {
            skuId,
            merchantId,
            eans
        });
        return response.data;
    }
);

export const checkEans = createAsyncThunk(
    `${slice.name}checkEans`,
    async ({ skuId, ean, merchantId }: { skuId: number; ean: string; merchantId: number }) => {
        const response = await axios.get(`/loolu/api/additionalSkuEans/check`, {
            params: {
                skuId,
                ean,
                merchantId
            }
        });
        return response.data.response;
    }
);

export const associateProduct = async ({
    merchantId,
    productId,
    idProvider,
    action
}: {
    merchantId: number | string;
    productId: number | string;
    idProvider: number | string;
    action: string;
}) => {
    try {
        const response =
            action === 'remove'
                ? await axios.delete(`/loolu/api/catalog/products/${productId}/vendor`, {
                      params: {
                          merchantId
                      }
                  })
                : await axios.put(`/loolu/api/catalog/products/${productId}/vendor`, {
                      merchantId,
                      productId,
                      idProvider
                  });

        return response;
    } catch (error) {}
};
